import React, {Fragment, useContext, useState, useEffect, useRef} from 'react'
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import Proptypes from 'prop-types'
import _ from "lodash"
import {SkeletonComponent} from "@syncfusion/ej2-react-notifications"
// ** Custom Components
import Avatar from '@components/avatar'

// ** Third Party Components
import classnames from 'classnames'
import Chart from 'react-apexcharts'
import { Card, CardBody, CardHeader, CardText, ButtonGroup, Button, UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import {CloudDownloadIcon} from '@csrc/assets/icons'

import {ThemeColors} from '@src/utility/context/ThemeColors'
import {trans} from '@utils'

import {getAvailableRanges, getSuitableData} from "../utility/utils"
import PopUpModal from "./PopUpModal"
import {AreaChart, BarChart, MultiLineChart, DonutChart, HorizontalBarChart, MixChart, StorageChart} from "./ApexChartOptions"

const ChartTypes = {
  area: "area",
  bar: "bar",
  donut: "pie",
  storage: "donut",
  mix: "line",
  "multi-line": "line",
  "horizontal-bar": "bar"
}

const StatisticCard = props => {
  // ** Props
  const { icon, color, showStats, stats, statTitle, statTitleRender, series, options, chartType, height, className, hasCardBody, hasGrouping, vertical = false, hasChart = true, renderChartRightHeader: RenderChartRightHeader, renderChartFilters: RenderChartFilters, renderChartLegends :RenderChartLegends, range, showRangesAvailability, showExport, rangesAvailability, extraProps, ...rest } = props
  const {showCsvExport = true} = extraProps
  const {dataRange, setDataRange} = range

  const type = ChartTypes[chartType]

  const chartRef = useRef(null)

  useEffect(() => {
    try {
      if (chartRef?.current?.chart) {
        chartRef?.current?.chart?.updateOptions({series, ...options, title: { text: showStats ? stats : statTitle }, subtitle: { text: showStats ? statTitle : '' }})
        chartRef?.current?.chart?.updateOptions({series, ...options, title: { text:  ''}, subtitle: {text: ''}})
      }
    } catch (e) {

    }
  }, [chartRef?.current?.chart])

  if (!vertical) {
    return (
      <Card {...rest}>
        <CardHeader className='align-items-start pb-0'>
          {
            type === 'pie' ? (
              <Fragment>
                {_.isNil(statTitleRender) && (
                  <div>
                    {statTitle && <CardText>{statTitle}</CardText>}
                  </div>
                )}
                {!_.isNil(statTitleRender) && statTitleRender}
              </Fragment>
            ) : (
              <div>
                {_.isNil(statTitleRender) && (
                  <Fragment>
                    {showStats && <h2 className='font-weight-bolder'>{stats}</h2>}
                    {statTitle && <CardText>{statTitle}</CardText>}
                  </Fragment>
                )}
                {!_.isNil(statTitleRender) && statTitleRender}
              </div>
            )
          }
          {
            icon && (
              <Avatar className='avatar-stats p-50 m-0' color={`light-${color}`} icon={icon} />
            )
          }
          <div>
            { showRangesAvailability &&
              (hasGrouping && rangesAvailability > 1) && (
                  <ButtonGroup size={'sm'}>
                    <Button outline color={(dataRange === 'days') ? 'primary' : 'secondary'} onClick={ () => setDataRange('days')}>{trans('analytics.inputs.days')}</Button>
                    {
                      rangesAvailability > 1 && (
                        <Button outline color={(dataRange === 'months') ? 'primary' : 'secondary'} onClick={ () => setDataRange('months')}>{trans('analytics.inputs.months')}</Button>
                      )
                    }
                    {
                      rangesAvailability > 2 && (
                        <Button outline color={(dataRange === 'years') ? 'primary' : 'secondary'} onClick={ () => setDataRange('years')}>{trans('analytics.inputs.years')}</Button>
                      )
                    }
                  </ButtonGroup>
                )
            }
            {!_.isNil(RenderChartRightHeader) && RenderChartRightHeader}
            {
              (showExport && type !== 'area') && (
                <UncontrolledButtonDropdown className={'ml-50'} size={'sm'}>
                  <DropdownToggle className={'px-50'} size={'sm'} outline style={{borderRadius: '0.358rem'}} color={'secondary'}>
                    <FontAwesomeIcon icon={CloudDownloadIcon} className='m-0'/>
                  </DropdownToggle>
                  <DropdownMenu tag='ul' right>
                    <DropdownItem className={'w-100'} onClick={() => {
                      chartRef?.current?.chart?.updateOptions({series, ...options, title: { text:  showStats ? stats : statTitle}, subtitle: {text: showStats ? statTitle : ''}, legend: { show: true }})
                      setTimeout(() => {
                        chartRef?.current?.chart?.exports?.exportToPng()
                        chartRef?.current?.chart?.updateOptions({series, ...options, title: { text:  ''}, subtitle: {text: ''}})
                      }, [100])
                    }}>
                      <span className='ml-25'>{trans('analytics.charts.actions.exportToPng')}</span>
                    </DropdownItem>
                    <DropdownItem className={'w-100'} onClick={() => {
                      chartRef?.current?.chart?.updateOptions({series, ...options, title: { text:  showStats ? stats : statTitle}, subtitle: {text: showStats ? statTitle : ''}, legend: { show: true }})
                      setTimeout(() => {
                        chartRef?.current?.chart?.exports?.exportToSVG()
                        chartRef?.current?.chart?.updateOptions({series, ...options, title: { text:  ''}, subtitle: {text: ''}})
                      }, [100])
                    }}>
                      <span className='ml-25'>{trans('analytics.charts.actions.exportToSvg')}</span>
                    </DropdownItem>
                    {showCsvExport && (
                      <DropdownItem className={'w-100'} onClick={() => chartRef.current.chart.exports.exportToCSV({series, columnDelimiter: ','})}>
                        <span className='ml-25'>{trans('analytics.charts.actions.exportToCsv')}</span>
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                </UncontrolledButtonDropdown>
              )
            }
          </div>
        </CardHeader>
        {
          hasCardBody ? (
            <CardBody>
              {
                hasChart && (
                  <div className={'chart-container'}>
                    {!_.isNil(RenderChartFilters) && RenderChartFilters}
                    <Chart ref={chartRef} options={options} series={series} type={type} height={height} />
                    {!_.isNil(RenderChartLegends) && RenderChartLegends}
                  </div>
                )
              }
            </CardBody>
          ) : (
            hasChart && (
              <div className={'chart-container'}>
                {!_.isNil(RenderChartFilters) && RenderChartFilters}
                <Chart ref={chartRef} options={options} series={series} type={type} height={height} />
                {!_.isNil(RenderChartLegends) && RenderChartLegends}
              </div>
            )
          )
        }
      </Card>
    )
  }

  return (
    <Card {...rest}>
      <CardBody
        className={classnames('pb-0', {
          [className]: className
        })}
      >
        {
          icon && (
            <Avatar className='avatar-stats p-50 m-0' color={`light-${color}`} icon={icon} />
          )
        }
        <h2 className='font-weight-bolder mt-1'>{stats}</h2>
        <p className='card-text'>{statTitle}</p>
      </CardBody>
      {
        hasChart && (
          <Chart ref={chartRef} options={options} series={series} type={type} height={height} />
        )
      }
    </Card>
  )
}

const Statistic = (props) => {

  const context = useContext(ThemeColors)
  const [modal, setModal] = useState({show: false, data: {}})
  const [dataRange, setDataRange] = useState('days')

  const {
    showStats = true,
    stats,
    color = 'warning',
    title,
    titleRender = null,
    options = {},
    icon = undefined,
    hasChart = true,
    chartType = 'area',
    renderChartRightHeader = null,
    renderChartFilters = null,
    renderChartLegends = null,
    vertical = false,
    loading = false,
    height = 250,
    skeletonMinHeight,
    detailsType,
    ToolbarDetailsComponent,
    DetailsComponent,
    isDecimal,
    showRangesAvailability = true,
    calculateAverageInGrouping = false,
    showExport = true,
    hasCardBody = false,
    hasGrouping = false,
    exportOptions = {},
    extraProps = {}
  } = props

  useEffect(() => {
    if (loading) {
      setDataRange('days')
    }
  }, [loading])

  if (loading) {
    return (
      <div className={'h-100 m-50'} style={{minHeight: skeletonMinHeight ? skeletonMinHeight : height + 78}}>
        <SkeletonComponent id='skeletonRectangle' shape='Rectangle' width='100%' height='100%'></SkeletonComponent>
      </div>
    )
  }

  const chartOptions = () => {
    switch (chartType) {
      case 'area':
        return AreaChart(props.data, dataRange, context, color, options, isDecimal, calculateAverageInGrouping, height, exportOptions, extraProps)
      case 'bar':
        return BarChart(props.data, dataRange, setModal, context, color, options, detailsType, title, ToolbarDetailsComponent, DetailsComponent, isDecimal, calculateAverageInGrouping, height, exportOptions, extraProps)
      case 'multi-line':
        return MultiLineChart(props.data, dataRange, setModal, context, color, options, detailsType, title, ToolbarDetailsComponent, DetailsComponent, height, exportOptions, extraProps)
      case 'horizontal-bar':
        return HorizontalBarChart(props.data, setModal, context, color, options, detailsType, title, ToolbarDetailsComponent, DetailsComponent, height, exportOptions, extraProps)
      case 'donut':
        return DonutChart(props.data, setModal, detailsType, title, ToolbarDetailsComponent, DetailsComponent, isDecimal, height, exportOptions, extraProps)
      case 'mix':
        return MixChart(props.data, dataRange, setModal, context, color, options, detailsType, title, ToolbarDetailsComponent, DetailsComponent, height, exportOptions, extraProps)
      case 'storage':
        return StorageChart(props.data, dataRange, context, color, options, isDecimal, calculateAverageInGrouping, height, exportOptions, extraProps)
    }
  }

  return (
    <>
      <StatisticCard
        icon={icon}
        color={color}
        showStats={showStats}
        stats={`${stats}`}
        statTitle={title}
        statTitleRender={titleRender}
        series={chartOptions().series}
        chartType={chartType}
        options={chartOptions().options}
        hasChart={hasChart}
        renderChartRightHeader={renderChartRightHeader}
        renderChartFilters={renderChartFilters}
        renderChartLegends={renderChartLegends}
        vertical={vertical}
        height={height}
        range={{dataRange, setDataRange}}
        showRangesAvailability={showRangesAvailability}
        rangesAvailability={showRangesAvailability && getAvailableRanges(props.data)}
        showExport={showExport}
        hasCardBody={hasCardBody}
        hasGrouping={hasGrouping}
        extraProps={extraProps}
      />
      {
        modal.show && (
          <PopUpModal data={modal.data} onClose={() => setModal({show: false, data: null})} />
        )
      }
    </>
  )
}


Statistic.propTypes = {
  // stats: Proptypes.number | Proptypes.string,
  color: Proptypes.string,
  title: Proptypes.string,
  chartType: Proptypes.string,
  icon: Proptypes.element,
  // data: Proptypes.array,
  loading: Proptypes.bool
}

export default Statistic
